import { motion } from "framer-motion";
import { Brain, Target, Repeat, Trophy } from "lucide-react";

const ValueSection = () => {
  const psychologyPoints = [
    {
      icon: Brain,
      title: "Sunk Cost Effect",
      description: "Customers don't want to 'waste' the points they've already earned. They'll return to use them.",
      stat: "73%",
      statLabel: "return to avoid losing points",
    },
    {
      icon: Target,
      title: "Goal Gradient Effect",
      description: "The closer customers get to a reward, the more motivated they become to reach it.",
      stat: "82%",
      statLabel: "speed up when near goal",
    },
    {
      icon: Repeat,
      title: "Habit Formation",
      description: "Regular earning and checking creates a behavioral loop that becomes automatic.",
      stat: "3x",
      statLabel: "more visits per month",
    },
    {
      icon: Trophy,
      title: "Achievement Drive",
      description: "Unlocking rewards triggers dopamine, making the experience emotionally rewarding.",
      stat: "91%",
      statLabel: "feel positive about brand",
    },
  ];

  return (
    <section 
      className="section-padding bg-gradient-to-br from-indigo-50 via-white to-amber-50"
      data-testid="value-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-50 text-amber-700 text-sm font-medium mb-4 border border-amber-200">
            The Psychology of Loyalty
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="value-headline"
          >
            Why Customers <span className="gradient-text">Actually</span> Return
          </h2>
          <p className="text-lg text-slate-600">
            It's not about the discount. It's about the psychology of progress, achievement, and not wanting to lose what you've earned.
          </p>
        </motion.div>

        {/* Psychology cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {psychologyPoints.map((point, index) => (
            <motion.div
              key={point.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-feature group"
              data-testid={`psychology-${point.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start gap-5">
                <div className="icon-container-gold flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <point.icon className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 
                    className="text-xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {point.title}
                  </h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">
                    {point.description}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold gradient-text" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {point.stat}
                    </span>
                    <span className="text-sm text-slate-500">{point.statLabel}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Key insight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <div className="relative bg-slate-900 rounded-3xl p-8 lg:p-12 overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl" />

            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h3 
                className="text-2xl lg:text-3xl font-bold text-white mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                The Key Insight
              </h3>
              <p className="text-xl lg:text-2xl text-slate-300 leading-relaxed">
                Customers don't return because you're <span className="text-red-400 line-through">cheap</span>. 
                They return because they have <span className="text-amber-400 font-semibold">something to gain</span>.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span>Points waiting to be used</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span>Progress they don't want to lose</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-2 h-2 rounded-full bg-indigo-400" />
                  <span>Rewards within reach</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValueSection;
